import {AfterViewInit, Component, effect, OnDestroy, Signal, TemplateRef, ViewChild} from '@angular/core';
import {ReceiptService} from "../../services/receipt/receipt.service";
import {Receipt} from "../../models/receipt";
import {DatePipe} from "@angular/common";
import {RouterLink} from "@angular/router";
import {Household} from "../../models/household";
import {ContextService} from "../../services/context/context-signal.service";
import {Context} from "../../models/context";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faFile, faFileCircleExclamation, faUpload} from "@fortawesome/free-solid-svg-icons";
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {PageAction} from '../../components/app-shell/page-action.model';
import {
  UiBadgeComponent,
  UiFileInputComponent,
  UiListComponent,
  UiListItemComponent,
  UiTooltipDirective
} from '../../shared/ui';
import { AppLogger, AppLoggerService } from '../../core/logging';

@Component({
  selector: 'app-receipts',
  standalone: true,
  imports: [
    DatePipe,
    RouterLink,
    FaIconComponent,
    UiListComponent,
    UiListItemComponent,
    UiFileInputComponent,
    UiBadgeComponent,
    UiTooltipDirective
  ],
  templateUrl: './receipts.component.html',
  styleUrl: './receipts.component.scss'
})
export class ReceiptsComponent implements AfterViewInit, OnDestroy {

  context: Signal<Context> = this.contextService.context;

  receipts: Receipt[];
  household: Household;

  isUploading = false;
  selectedFile: File | null = null;

  @ViewChild('filePickerAction') filePickerActionTemplate!: TemplateRef<any>;

  private readonly log: AppLogger;

  constructor(
    private receiptService: ReceiptService,
    private contextService: ContextService,
    private pageHeaderService: PageHeaderService,
    private appLogger: AppLoggerService
  ) {
    this.log = this.appLogger.createForClass(ReceiptsComponent);

    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;

      this.household = c.household;
      this.loadHouseholdReceipts(this.household.id);
    });

  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.pageHeaderService.setPageTitle("Receipts");
      this.refreshPageActions();
    }, 0);
  }

  /**
   * (Re)publish toolbar actions. Both actions are pinned so they always show
   * (full-width second row on mobile, inline on desktop):
   *  - file picker: custom widget (template) → always visible
   *  - upload button: disabled until a file is selected, shows a loading
   *    spinner while the upload is in progress
   */
  private refreshPageActions(): void {
    const actions: PageAction[] = [
      {
        type: 'custom',
        id: 'pick-file',
        template: this.filePickerActionTemplate,
        pinned: true,
      },
      {
        type: 'button',
        id: 'upload',
        label: 'Upload',
        icon: faUpload,
        variant: 'primary',
        pinned: true,
        disabled: !this.selectedFile || this.isUploading,
        loading: this.isUploading,
        onClick: () => this.uploadReceipt(),
      },
    ];
    this.pageHeaderService.setPageActions(actions);
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearAll();
  }

  loadHouseholdReceipts(householdId: number): void {
    this.receiptService.receipts(householdId).subscribe(receipts => {
      this.receipts = receipts;
    });
  }

  onFileSelected(file: File | null): void {
    this.selectedFile = file;
    // Re-publish so the Upload button's disabled flag reflects the selection.
    this.refreshPageActions();
  }

  uploadReceipt(): void {
    if (this.isUploading) {
      return;
    }

    const file = this.selectedFile;
    if (file) {
      this.isUploading = true;
      this.refreshPageActions();
      this.receiptService.uploadReceipt(this.household.id, file).subscribe({
        next: () => {
          this.selectedFile = null;
          this.loadHouseholdReceipts(this.household.id);
        },
        error: (error) => {
          this.log.error('Error uploading receipt file', error, { householdId: this.household?.id });
        },
        complete: () => {
          this.isUploading = false;
          this.refreshPageActions();
        }
      });
    } else {
      this.log.warn('No file selected for upload');
    }
  }

  importStatusVariant(status: Receipt['import_status'] | null | undefined): UiBadgeComponent['variant'] {
    switch (status) {
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      case 'ready':
        return 'success';
      case 'finished':
        return 'info';
      case 'unknown':
      default:
        return 'neutral';
    }
  }

  protected readonly faFileIndicator = faFile;
  protected readonly faFileNotFound = faFileCircleExclamation;
}
